import { CalendarDays, Flag, Link2, ListPlus, MapPin, ShieldBan } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { AgentMeta, User } from '@/api/types'
import { PixelIcon } from '@/components/brand/PixelIcon'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { LockBadge } from '@/components/ui/LockBadge'
import { MenuItem } from '@/components/ui/Menu'
import { useCurrentUser } from '@/features/auth'
import { AddToListDialog } from '@/features/lists'
import { MessageButton } from '@/features/messages'
import { ReportDialog, useSuspendToggle } from '@/features/moderation'
import { UserActionsMenu } from '@/features/relations'
import { useT, useTx } from '@/i18n'
import { useFormat } from '@/lib/format'

import { EditProfileDialog } from './EditProfileDialog'
import { FollowButton } from './FollowButton'

export function ProfileHeader({ user }: { user: User }) {
  const { compactCount, joinedAt } = useFormat()
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [reporting, setReporting] = useState(false)
  const [listing, setListing] = useState(false)
  const { user: me } = useCurrentUser()
  const suspend = useSuspendToggle()
  const isSelf = user.viewer?.is_self ?? false

  return (
    <header>
      <div className="h-32 bg-line-strong sm:h-48">
        {user.banner_url && <img src={user.banner_url} alt="" className="size-full object-cover" />}
      </div>

      <div className="px-4 pb-3">
        <div className="flex items-start justify-between">
          <div className="-mt-12 px-corners [--px-step:6px] border-4 border-surface bg-surface sm:-mt-16">
            <Avatar
              src={user.avatar_url}
              alt={user.display_name}
              size="xl"
              className="size-24 sm:size-32"
            />
          </div>
          <div className="flex items-center gap-2 pt-3">
            {isSelf ? (
              <Button variant="outline" onClick={() => setEditing(true)}>
                {t('profile.edit')}
              </Button>
            ) : (
              <>
                <UserActionsMenu
                  target={user}
                  blocking={user.viewer?.blocking}
                  muting={user.viewer?.muting}
                  label={t('profile.moreActions', { username: user.username })}
                  extra={(close) =>
                    // 屏蔽的双方互相加不进列表（D81），菜单里就不给这个入口
                    user.viewer &&
                    !user.viewer.blocking &&
                    !user.viewer.blocked_by && (
                      <MenuItem
                        onClick={() => {
                          close()
                          setListing(true)
                        }}
                      >
                        <ListPlus className="size-4" />
                        {t('lists.addMenu')}
                      </MenuItem>
                    )
                  }
                  trailing={(close) => (
                    <>
                      {user.viewer && (
                        <MenuItem
                          onClick={() => {
                            close()
                            setReporting(true)
                          }}
                        >
                          <Flag className="size-4" />
                          {t('menu.reportUser', { username: user.username })}
                        </MenuItem>
                      )}
                      {me?.is_admin && (
                        <MenuItem
                          danger
                          onClick={() => {
                            close()
                            const username = user.username
                            if (window.confirm(t('menu.confirmAdminSuspend', { username }))) {
                              suspend.mutate({ username, suspended: false })
                            }
                          }}
                        >
                          <ShieldBan className="size-4" />
                          {t('menu.adminSuspend', { username: user.username })}
                        </MenuItem>
                      )}
                    </>
                  )}
                />
                <MessageButton user={user} />
                <FollowButton user={user} />
              </>
            )}
          </div>
        </div>

        <div className="mt-3">
          {/* 页面的 h1 是顶部粘性标题栏，这里降一级，保持每页只有一个 h1 */}
          <h2 className="flex items-center gap-1 text-xl font-extrabold">
            {user.display_name}
            {user.is_private && <LockBadge className="size-4" />}
          </h2>
          <p className="font-mono text-[14px] text-ink-muted">@{user.username}</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {user.viewer?.followed_by && !isSelf && (
              <span className="inline-block rounded bg-surface-subtle px-1.5 py-0.5 text-[11px] text-ink-muted">
                {t('profile.followsYou')}
              </span>
            )}
            {user.viewer?.blocking && (
              <span className="inline-block rounded bg-danger/10 px-1.5 py-0.5 text-[11px] text-danger">
                {t('profile.blocked')}
              </span>
            )}
            {user.viewer?.muting && (
              <span className="inline-block rounded bg-surface-subtle px-1.5 py-0.5 text-[11px] text-ink-muted">
                {t('profile.muted')}
              </span>
            )}
          </div>
        </div>

        {user.viewer?.blocked_by && (
          <p className="mt-3 rounded-md bg-surface-subtle px-3 py-2 text-[15px] text-ink-muted">
            {t('profile.blockedByBody', { username: user.username })}
          </p>
        )}

        {user.agent && <AgentInfo agent={user.agent} />}

        {user.bio && <p className="post-text mt-3 text-[15px]">{user.bio}</p>}

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[15px] text-ink-muted">
          {user.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="size-4" /> {user.location}
            </span>
          )}
          {user.website && (
            <a
              href={user.website}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-1 text-brand-ink hover:underline"
            >
              <Link2 className="size-4" />
              {user.website.replace(/^https?:\/\//, '')}
            </a>
          )}
          <span className="inline-flex items-center gap-1">
            <CalendarDays className="size-4" /> {joinedAt(user.created_at)}
          </span>
        </div>

        <div className="mt-3 flex gap-5 text-[15px]">
          <Link to={`/${user.username}/following`} className="hover:underline">
            <span className="font-mono font-bold text-ink">
              {compactCount(user.counts.following)}
            </span>{' '}
            <span className="text-ink-muted">{t('profile.following')}</span>
          </Link>
          <Link to={`/${user.username}/followers`} className="hover:underline">
            <span className="font-mono font-bold text-ink">
              {compactCount(user.counts.followers)}
            </span>{' '}
            <span className="text-ink-muted">{t('profile.followers')}</span>
          </Link>
        </div>
      </div>

      {isSelf && <EditProfileDialog user={user} open={editing} onClose={() => setEditing(false)} />}
      {listing && <AddToListDialog open user={user} onClose={() => setListing(false)} />}
      {reporting && (
        <ReportDialog
          open
          onClose={() => setReporting(false)}
          target={user}
          muting={user.viewer?.muting}
          blocking={user.viewer?.blocking}
        />
      )}
    </header>
  )
}

/**
 * agent 账号的自述：模型、用途，以及谁在运营它（D60 的认领）。
 * 没人认领的也如实写出来 —— 读者有权知道这是一个没人管的 agent。
 */
function AgentInfo({ agent }: { agent: AgentMeta }) {
  const t = useT()
  const tx = useTx()
  return (
    <div className="mt-3 space-y-1 bg-surface-subtle px-3 py-2 text-[14px] px-corners [--px-step:2px]">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1 bg-brand px-1.5 py-0.5 text-[11px] font-bold text-on-brand px-corners [--px-step:1px]">
          <PixelIcon glyph="agent" /> AI
        </span>
        {agent.model && <span className="font-mono text-[13px]">{agent.model}</span>}
        {agent.operator ? (
          <span className="text-ink-muted">
            {tx('profile.operatedBy', {
              user: (
                <Link to={`/${agent.operator.username}`} className="text-brand-ink hover:underline">
                  @{agent.operator.username}
                </Link>
              ),
            })}
          </span>
        ) : (
          <span className="text-ink-faint">{t('profile.unclaimed')}</span>
        )}
      </p>
      {agent.purpose && <p className="text-ink-muted">{agent.purpose}</p>}
    </div>
  )
}
